'use client'

import {Grid, Icon, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import useStyles from "@/hooks/useStyles";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {settingsSelectors, updateSidebarItems, updateSidebarStatus} from "@/redux/slices/settings";
import {Menu, MenuItem, Sidebar, SubMenu} from "react-pro-sidebar";
import MenuIcon from '@mui/icons-material/Menu';
import Link from "next/link";
import {usePathname} from "next/navigation";
import {useGetUserModulesQuery} from "@/redux/rtk/admin/shared";
import {SLUGGED_PROPS} from "@/components/layout/types";
import {ensureLeadingSlash} from "@/components/shared/client/utils";

const subMenuStyles = {
    ".ps-submenu-content": {
        backgroundColor: "inherit"
    },
    ".ps-menu-root": {
        "& hr": {
            borderColor: theme => `${theme.palette?.texts?.sidebarDivider}!important`
        }
    }
}

const iconFontSize = {
    fontSize: "1.2rem!important"
}

const getUniqueValues = (arr, key) => {
    const seen = new Set();
    return arr
        .map(obj => obj[key])
        .filter(value => {
            if (seen.has(value)) return false;
            seen.add(value);
            return true;
        });
};

const getModuleChildren = (moduleRecords: SLUGGED_PROPS[]) => {
    if (moduleRecords?.length) {
        let childrenTree = {};
        for (let rec of moduleRecords) {
            childrenTree[`${rec.uid}`] = {
                ...rec,
                children: moduleRecords.filter(a => a.pid === rec.moduleId)
            }
        }
        return childrenTree;
    }
    return {}
}

const checkIfOpen = (sidebarItems: SLUGGED_PROPS[], rec: SLUGGED_PROPS, navPage?: string) => {
    const t: SLUGGED_PROPS[] = sidebarItems.filter(b => b.pid === rec.moduleId);
    if (t?.length) {
        for (let q of t) {
            if(checkIfOpen(sidebarItems, q, navPage)) {
                return true;
            }
        }
    } else {
        if (rec.navLabel === navPage) {
            return true
        }
    }
    return false;
}

const GetSubMenuItems = ({rec, children, childrenTree}: {
    rec: SLUGGED_PROPS;
    children: SLUGGED_PROPS[];
    childrenTree: { [key: string]: { children: SLUGGED_PROPS[] } }
}) => {
    const {webContext, sidebarItems} = useSelector((state) => {
        const webContext = settingsSelectors.getWebContext(state);
        const sidebarItems = settingsSelectors.getSidebarItems(state);
        return {webContext, sidebarItems};
    }, shallowEqual);
    const [open, setOpen] = useState<boolean>(checkIfOpen(sidebarItems, rec, webContext?.navPage));

    useEffect(() => {
        if(sidebarItems?.length && webContext?.navPage) {
            setOpen(checkIfOpen(sidebarItems, rec, webContext?.navPage))
        }
    }, [sidebarItems, webContext])

    const {sidebarIconColor, sidebarTextColor, sidebarFocusedTextColor} = useStyles()
    return <>
        <SubMenu
            open={open}
            onOpenChange={(isOpen) => {
                setOpen(isOpen)
            }}
            style={{...sidebarTextColor}}
            icon={<Icon sx={{...sidebarIconColor, ...iconFontSize}}>{rec.icon}</Icon>}
            label={rec.name}>
            {children.map(a => {
                const childs = childrenTree?.[`${a.uid}`]?.children;
                return <div key={`${a.uid}`}>
                    {childs?.length <= 0 && <MenuItem
                        style={{
                            ...iconFontSize,
                            ...(a.navLabel === webContext?.navPage ? {
                                ...sidebarFocusedTextColor,
                                fontWeight: "bold"
                            } : sidebarTextColor)
                        }}
                        component={a?.link && <Link href={`${ensureLeadingSlash(a.link)}`}/>}
                        icon={<Icon sx={{...sidebarIconColor, ...iconFontSize}}>{a.icon}</Icon>}
                    >
                        {a.name}
                    </MenuItem>}
                    {childs?.length > 0 &&
                        <GetSubMenuItems
                            rec={a}
                            children={childs}
                            childrenTree={childrenTree}
                        />
                    }
                </div>
            })}
        </SubMenu>
    </>
}

const setModuleRecords = (moduleRecords: SLUGGED_PROPS[], index = 0) => {
    if (moduleRecords?.length) {
        let sluggedRecords = {};
        const slugs = getUniqueValues(moduleRecords, 'slug')
        if (slugs?.length) {
            for (let slug of slugs) {
                sluggedRecords[slug] = moduleRecords.filter(moduleRecord => moduleRecord.slug === slug)
            }
        }
        return sluggedRecords;
    }
    return {}
}

const CustomSidebar = ({adjustPadding, dir}: { adjustPadding: (key: number) => void; dir: string }) => {

    const {t} = useTranslation("dashboard");
    const [open, setOpen] = useState<boolean>(false);
    const {sidebarIconColor, sidebarTextColor, sidebarFocusedTextColor} = useStyles()
    const theme = useTheme();
    const dispatch = useDispatch();
    const elementRef = useRef<HTMLDivElement>(null);
    const pathName = usePathname();
    const {data, error, isLoading} = useGetUserModulesQuery(null);

    const sluggedModules = setModuleRecords(data?.data?.moduleRecords);
    const childrenTree = getModuleChildren(data?.data?.moduleRecords);

    const {webContext, sidebarStatus} = useSelector((state) => {
        const webContext = settingsSelectors.getWebContext(state);
        const sidebarStatus = settingsSelectors.getSidebarStatus(state);
        const theme = settingsSelectors.getTheme(state);
        const sidebarItems = settingsSelectors.getSidebarItems(state);
        return {webContext, theme, sidebarStatus, sidebarItems};
    }, shallowEqual);

    useEffect(() => {
        if (data?.data?.moduleRecords) {
            dispatch(updateSidebarItems(data.data.moduleRecords as SLUGGED_PROPS[]))
        }
    }, [data])

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                adjustPadding(entry.contentRect.width);
            }
        });

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return () => observer.disconnect();
    }, [dir, pathName]);

    const handleSidebarStatus = () => {
        const w = window.innerWidth
        if (w > 750) setOpen(!open);
        else dispatch(updateSidebarStatus(null))
    }

    return <Grid ref={elementRef} className={`sidebar he1 df pf ${dir === "ltr" ? "tpl" : "tpr"}`}
                  sx={{...subMenuStyles, maxHeight: "100vh", zIndex: 101}}>
        <Sidebar
            width={"18rem"}
            collapsed={open}
            backgroundColor={theme.palette?.backgrounds?.sidebar}
            style={{border: "none"}}
            breakPoint={"md"}
            toggled={sidebarStatus}
            rtl={dir === "rtl"}
        >
            <Menu iconShape="square" style={{height: "100%"}}
                  rootStyles={{
                      ".ps-menu-button": {
                          '&:hover': {
                              backgroundColor: theme.palette?.hovers?.sidebar_icons
                          },
                      },
                      ".ps-menuitem-root": {
                          background: theme.palette?.backgrounds?.sidebar
                      }
                  }}
            >
                <MenuItem
                    icon={<MenuIcon/>}
                    onClick={() => {
                        handleSidebarStatus()
                    }}
                    style={{
                        color: theme.palette?.texts?.sidebar_main,
                        borderBottom: `1px solid ${theme.palette?.borders?.mainDivider}`,
                        height: "3.5rem"
                    }}
                >
                    <h2>{t('global:adminPanel' as any)}</h2>
                </MenuItem>

                {Object.keys(sluggedModules ?? {}).map(key => {
                    return <div key={`${key}`}>
                        {
                            (sluggedModules[key].length > 0 && key !== 'noTag') &&
                            <MenuItem
                                style={{
                                    fontWeight: "bold",
                                    fontSize: !open ? "inherit" : "0.8rem"
                                }}>{t(`sidebar.${key}`)}
                            </MenuItem>
                        }
                        {
                            sluggedModules[key].length > 0 &&
                            sluggedModules[key].map((rec: SLUGGED_PROPS) => {
                                return <div key={`${rec.uid}`}>
                                    {(rec.pid === 123456789 && childrenTree?.[`${rec.uid}`]?.children?.length <= 0) &&
                                        <MenuItem
                                            style={{
                                                ...iconFontSize,
                                                ...(rec.navLabel === webContext?.navPage ? {
                                                    ...sidebarFocusedTextColor,
                                                    fontWeight: "bold"
                                                } : sidebarTextColor)
                                            }}
                                            component={rec?.link && <Link href={`${ensureLeadingSlash(rec.link)}`}/>}
                                            icon={<Icon sx={{
                                                ...iconFontSize,
                                                ...sidebarIconColor
                                            }}>{rec.icon}</Icon>}
                                        >
                                            {t(rec.name)}
                                        </MenuItem>}
                                    {(rec.pid === 123456789 && childrenTree?.[`${rec.uid}`]?.children?.length > 0) &&
                                        <GetSubMenuItems
                                            rec={rec}
                                            children={childrenTree[`${rec.uid}`].children}
                                            childrenTree={childrenTree}
                                        />
                                    }
                                </div>
                            })
                        }
                    </div>
                })}


                {/*{<MenuItem style={{fontWeight: "bold", fontSize: !open ? "inherit" : "0.8rem"}}>{t('admin')}</MenuItem>}*/}
                {/*<MenuItem style={{...sidebarTextColor}} component={<Link href="/admin/dashboard"/>}*/}
                {/*          icon={<DashboardIcon sx={{...sidebarIconColor}}/>}>{t('dashboard')}</MenuItem>*/}

                {/*<SubMenu style={{...sidebarTextColor}} icon={<Icon sx={{...sidebarIconColor}}>settings</Icon>}*/}
                {/*         label={t('settings')}>*/}
                {/*    <MenuItem style={{...sidebarTextColor}}*/}
                {/*              component={<Link href="/admin/modules/admin/settings/modules"/>}*/}
                {/*              icon={<Icon sx={{...sidebarIconColor}}>view_module</Icon>}>{t('modules')}</MenuItem>*/}
                {/*</SubMenu>*/}

                {/*{<hr color={"#f1f1f1"}/>}*/}
                {/*{<MenuItem*/}
                {/*    style={{fontWeight: "bold", fontSize: !open ? "inherit" : "0.8rem"}}>{t('modules')}</MenuItem>}*/}
                {/*<MenuItem style={{...sidebarTextColor}} component={<Link href="/admin/users"/>}*/}
                {/*          icon={<PeopleIcon sx={{...sidebarIconColor}}/>}>{t('users')}</MenuItem>*/}


            </Menu>
        </Sidebar>
    </Grid>
}

export default CustomSidebar;